import { useEffect, useState } from 'react'

import { ApiError, api } from '../api'
import type { JournalEntryDto, JournalListResponse, JournalSummaryResponse } from '../../contracts/api'
import { plural } from '../dates'
import { todayLabel } from './Today'
import './journal.css'

/** Откуда запись: человек должен узнать свою отметку через месяц. */
const SOURCE: Record<JournalEntryDto['kind'], string> = {
  day: 'карточка дня',
  week: 'пересечение недели',
  period: 'период',
  oracle: 'вопрос оракулу',
}

const RESONANCE: Record<'yes' | 'partly' | 'no', { sign: string; word: string }> = {
  yes: { sign: '●', word: 'отозвалось' },
  partly: { sign: '◐', word: 'отчасти' },
  no: { sign: '○', word: 'нет' },
}

/**
 * Записи приходят от новых к старым. Группируем подряд идущие в один день:
 * дата — единственная опора ленты, без неё отметки сливаются в список без
 * времени.
 */
function byDay(entries: JournalEntryDto[]): { day: string; items: JournalEntryDto[] }[] {
  const days: { day: string; items: JournalEntryDto[] }[] = []
  for (const entry of entries) {
    const last = days.at(-1)
    if (last && last.day === entry.day) last.items.push(entry)
    else days.push({ day: entry.day, items: [entry] })
  }
  return days
}

/**
 * Журнал: сводка за месяц и лента отметок. Смысл экрана — не «сколько я
 * отметил», а что из сказанного Атласом повторно подтверждается: поэтому
 * сверху счёт и признаки, а не календарь.
 */
export function Journal() {
  const [entries, setEntries] = useState<JournalEntryDto[] | null>(null)
  const [summary, setSummary] = useState<JournalSummaryResponse | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api
      .get<JournalListResponse>('/api/journal?limit=50')
      .then((result) => {
        if (!cancelled) setEntries(result.entries)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof ApiError ? cause.message : 'Журнал не открылся')
      })
    // Сводка необязательна: без неё лента всё равно читается.
    api
      .get<JournalSummaryResponse>('/api/journal/summary')
      .then((result) => {
        if (!cancelled) setSummary(result)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  if (error) return <div className="state">{error}</div>
  if (!entries) return <div className="state">Открываем журнал…</div>

  const confirmed = summary?.byKey.filter((item) => item.yes > 0) ?? []

  return (
    <div className="screen journal">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">журнал</div>
      </div>

      <h1 className="display">Журнал</h1>
      <div className="sub">Отозвалось или нет — и что из этого повторяется.</div>

      {entries.length === 0 ? (
        <div className="card journal-empty">
          <div className="lbl">Зачем это</div>
          <p>
            Атлас говорит о тебе много и уверенно. Проверить его может только один человек — ты сам: одно касание
            под карточкой дня, пересечением недели или разбором периода, и отметка остаётся здесь.
          </p>
          <p>
            Через месяц накопится не гороскоп, а факты: какие признаки твоей карты подтверждаются раз за разом, а
            какие — красивые слова, к которым ты не имеешь отношения.
          </p>
        </div>
      ) : (
        <>
          {summary && (
            <div className="card journal-summary">
              <div className="lbl">Итог</div>
              <p className="jsum-line">
                За 30 дней: {summary.days30.entries}{' '}
                {plural(summary.days30.entries, 'запись', 'записи', 'записей')} · отозвалось {summary.days30.yes} ·
                отчасти {summary.days30.partly} · нет {summary.days30.no}
              </p>
              {summary.streakDays > 0 && (
                <p className="jsum-streak">
                  {summary.streakDays} {plural(summary.streakDays, 'день', 'дня', 'дней')} подряд
                </p>
              )}
              {confirmed.length > 0 && (
                <div className="jsum-keys">
                  <div className="lbl">Что подтверждается</div>
                  {confirmed.map((item) => (
                    <div className="jsum-key" key={item.key}>
                      <span>{item.title}</span>
                      <span className="dim">
                        подтверждалась {item.yes} {plural(item.yes, 'раз', 'раза', 'раз')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="lbl sect">Отметки</div>
          <div className="journal-days">
            {byDay(entries).map((group) => (
              <div key={group.day}>
                <div className="lbl journal-day-head">{todayLabel(group.day)}</div>
                {group.items.map((entry) => {
                  const mark = entry.resonance ? RESONANCE[entry.resonance] : null
                  return (
                    <div className="journal-entry" key={entry.id}>
                      <div className="journal-entry-head">
                        <span className="journal-source">{SOURCE[entry.kind]}</span>
                        {mark && (
                          <span className={`journal-res ${entry.resonance}`}>
                            {mark.sign} {mark.word}
                          </span>
                        )}
                      </div>
                      {entry.note && <p className="journal-entry-note">{entry.note}</p>}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
